import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

const EMOTIONAL_STATES = [
  {
    id: 'angry',
    emoji: '😤',
    label: 'Angry',
    color: 'red',
    description: 'I feel upset and emotional about this situation',
    guidance: [
      'Take a few deep breaths before proceeding',
      'Consider waiting 10 minutes to review with a clear mind',
      'Your feelings are valid, but responding calmly leads to better outcomes',
      'This process is designed to be fair to both parties',
    ],
  },
  {
    id: 'confused',
    emoji: '😕',
    label: 'Confused',
    color: 'yellow',
    description: "I'm not sure what this is about or what to expect",
    guidance: [
      'Take your time to review the information carefully',
      'You can ask questions throughout the process',
      'An attorney will review any agreement before it\'s final',
      'You\'re not committing to anything by participating',
    ],
  },
  {
    id: 'worried',
    emoji: '😟',
    label: 'Worried',
    color: 'orange',
    description: "I'm concerned about the outcome",
    guidance: [
      'Your responses are private until a settlement is reached',
      'You control what you\'re willing to accept',
      'Nothing you share here can be used against you in court',
      'The process is designed to protect both parties',
    ],
  },
  {
    id: 'ready',
    emoji: '✅',
    label: 'Ready',
    color: 'green',
    description: "I'm calm and ready to participate constructively",
    guidance: [
      'Great mindset for productive resolution',
      'Be honest and thorough in your responses',
      'The AI will help find fair middle ground',
      'Focus on finding a solution that works for both sides',
    ],
  },
];

const PreparePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [selectedState, setSelectedState] = useState(null);
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDispute = async () => {
      try {
        const data = await api.getRespondView(token);
        setDispute(data);
      } catch (err) {
        setError(err.message || 'Failed to load dispute information');
      } finally {
        setLoading(false);
      }
    };

    fetchDispute();
  }, [token]);

  const handleContinue = async () => {
    if (!selectedState) {
      setError('Please select how you\'re feeling');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.prepareMode(token, selectedState);
      navigate(`/respond/${token}/assessment`);
    } catch (err) {
      setError(err.message || 'Failed to save your response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedStateData = EMOTIONAL_STATES.find(s => s.id === selectedState);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !dispute) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <ProgressBar current={1} total={4} phase="Emotional Check-In" />

      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Before We Begin</h1>
        <p className="text-gray-600">
          How are you feeling about this situation right now?
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Your answer helps us provide appropriate guidance
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {EMOTIONAL_STATES.map((state) => (
          <button
            key={state.id}
            onClick={() => setSelectedState(state.id)}
            className={`p-6 border-2 rounded-lg text-left transition-all ${
              selectedState === state.id
                ? `border-${state.color}-500 bg-${state.color}-50`
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            disabled={submitting}
          >
            <div className="flex items-start mb-3">
              <span className="text-4xl mr-3">{state.emoji}</span>
              <div>
                <h3 className="font-bold text-lg">{state.label}</h3>
                <p className="text-sm text-gray-600 mt-1">{state.description}</p>
              </div>
            </div>
            {selectedState === state.id && (
              <div className="ml-0 mt-3 pt-3 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  <p className="font-medium mb-2">Guidance for you:</p>
                  <ul className="space-y-1">
                    {state.guidance.map((tip, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      {selectedStateData && (
        <div className="card bg-blue-50 border-blue-200 mb-6">
          <div className="flex items-start">
            <div className="text-2xl mr-3">💡</div>
            <div>
              <h4 className="font-bold mb-2">Remember</h4>
              <p className="text-sm text-gray-700 mb-2">
                This process is designed to be fair and find solutions that work for both parties.
              </p>
              <p className="text-sm text-gray-700">
                Take your time, be honest, and focus on finding a resolution rather than being "right."
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => navigate(`/respond/${token}`)}
          className="btn-secondary"
          disabled={submitting}
        >
          Back
        </button>

        <button
          type="button"
          onClick={handleContinue}
          className="btn-primary"
          disabled={submitting || !selectedState}
        >
          {submitting ? 'Saving...' : 'Continue to Assessment'}
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500">
          Your emotional state is recorded privately to help provide appropriate guidance.
          It is not shared with the other party.
        </p>
      </div>
    </div>
  );
};

export default PreparePage;
